import React from "react";
import { useField, useFormikContext } from "formik";
import classNames from "classnames";
import FormGroup from "../formGroup";
import FormLabel from "../formLabel";
import Button from "shared/components/button";
import ErrorField from "../errorField";
import Variant from "shared/enums/Variant";
import CheckBox from "shared/form/core/checkbox";
import { FormCheckboxFieldGroupConfig, FormFieldOption } from "../types.d";
import Size from "shared/enums/Size";
import { useCopies } from "shared/hooks/useCopies";
import { isEmpty } from "shared/utils";
import FormOverwriteField from "../overwriteField";
import FieldOptionViewType from "shared/form/renderer/enums/FieldOptionViewType";
import { ConflictType } from "shared/form/renderer/enums/Conflict";

function FormCheckboxGroupField({ fullWidth = true, size = Size.MEDIUM, ...props }: FormCheckboxFieldGroupConfig) {
  const copies = useCopies();
  const defaultSelectAllText = copies.get("select.all");
  const defaultClearAllText = copies.get("clear.all");
  const { setFieldTouched, setFieldValue, validateForm, validateOnChange } = useFormikContext();
  const [field, meta] = useField({
    name: props.name,
    validate: props.validate
  });

  const changeHandler = React.useCallback(
    async (e, value) => {
      if (e.target.checked) {
        await setFieldValue(props.name, [...field.value, value.value], false);
      } else {
        await setFieldValue(
          props.name,
          field.value.filter((a: string) => a !== value.value),
          false
        );
      }
      if (!meta.touched) {
        await setFieldTouched(props.name, true, false);
      }
      validateOnChange && (await validateForm());
    },
    [field.value, props.name, setFieldTouched, setFieldValue, meta.touched, validateForm, validateOnChange]
  );

  const handleSelectAll = React.useCallback(
    async e => {
      e.preventDefault();
      await setFieldValue(
        props.name,
        props.options.map((item: FormFieldOption) => item.value)
      );
      if (!meta.touched) {
        await setFieldTouched(props.name, true);
      }
    },
    [props.name, props.options, setFieldTouched, setFieldValue, meta.touched]
  );

  const handleClearAll = React.useCallback(
    async e => {
      e.preventDefault();
      await setFieldValue(props.name, []);
      if (!meta.touched) {
        await setFieldTouched(props.name, true);
      }
    },
    [props.name, setFieldTouched, setFieldValue, meta.touched]
  );

  const computedClassNames = React.useMemo(() => {
    return classNames({
      checkboxGroup: true,
      "checkboxGroup--scroll": props.scroll,
      "checkboxGroup--noBorder": props.noBorder,
      "checkboxGroup--disabled": props.disabled,
      [`checkboxGroup--${size}`]: size,
      [`checkboxGroup--${props.variant}`]: props.variant
    });
  }, [props.disabled, props.noBorder, props.scroll, size, props.variant]);

  return (
    <React.Fragment>
      <FormGroup className={props.className} fullWidth={fullWidth} indented={props.indented}>
        <div className={computedClassNames}>
          <div className="checkboxGroup_labelWrapper">
            <FormLabel
              label={props.label}
              required={props.required}
              className="checkboxGroup_title"
              infoLabel={props.infoLabel}
            />
            {props.withSelectAll && (
              <div className="checkboxGroup_selectAllActionWrapper">
                <Button.Link
                  label={props.selectAllText || defaultSelectAllText}
                  onClick={handleSelectAll}
                  disabled={props.disabled}
                  variant={Variant.LINK}
                />
                <span>|</span>
                <Button.Link
                  label={props.clearAllText || defaultClearAllText}
                  onClick={handleClearAll}
                  disabled={props.disabled}
                  variant={Variant.LINK}
                />
              </div>
            )}
          </div>
          <ErrorField meta={meta} />
          <div
            className={classNames("checkboxGroup_wrapper", {
              // todo:sk update class name
              hasSingleOption:
                props.options.length === 1 || props.fieldOptionViewType === FieldOptionViewType.SINGLE_COLUMN
            })}>
            {props.options.map((item: FormFieldOption) => (
              <CheckBox
                name={item.value}
                checked={field.value?.includes("" + item.value)}
                onChange={e => changeHandler(e, item)}
                iconUrl={item.imageIcon?.url}
                label={item.label}
                key={item.value}
                disabled={props.disabled || props.disabledOptions?.includes(item.value)}
                size={size}
                tooltipContent={props.disabledOptions?.includes(item.value) ? props.disabledCheckboxTitle : undefined}
                classNameForLabel={item.customData?.className}
                conflictTagType={item.customData?.conflictTagType}
              />
            ))}
          </div>
          {!isEmpty(props.conflictValue) && (
            <FormOverwriteField
              forFieldName={props.name}
              disabled={props.disabled}
              conflictType={ConflictType.MULTISELECT}
              options={props.options}
            />
          )}
        </div>
      </FormGroup>
    </React.Fragment>
  );
}

export default FormCheckboxGroupField;
